import { Text } from "@asayinc/component-library";

import { PageContainer } from "src/components/containers/PageContainer/PageContainer";

import { SecondaryTabs, TableV2 as Table } from "@asayinc/component-library";
import { Stack } from "@mui/material";
import {
  History,
  HistoryOutlined,
  Verified,
  VerifiedOutlined,
  HourglassEmpty,
  HourglassEmptyOutlined,
} from "@mui/icons-material";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ROW_OPTIONS } from "src/constants/options/rowOptions";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useAdditionalMailingsQuery } from "src/store/additionalMailing";
import { getColumnData, getColumns, getRowData } from "./utils";
import { IAdditionalMailing } from "src/store/additionalMailing/types";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { ScrollContainer } from "src/components/containers/ScrollContainer";

export type AdditionalMailingsView =
  | "pending_approval"
  | "recently_approved"
  | "historical";

export const ADDITIONAL_MAILINGS_VIEW: {
  [key: string]: AdditionalMailingsView;
} = {
  PENDING_APPROVAL: "pending_approval",
  RECENTLY_APPROVED: "recently_approved",
  HISTORICAL: "historical",
};

export function AdditionalMailings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get(TABLE_SEARCH_PARAMS.VIEW) ||
    ADDITIONAL_MAILINGS_VIEW.PENDING_APPROVAL) as AdditionalMailingsView;

  const {
    getSort,
    offset,
    limit,
    ordering,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const { data, isFetching, isLoading } = useAdditionalMailingsQuery({
    offset,
    limit,
    ordering,
    tab: view || ADDITIONAL_MAILINGS_VIEW.PENDING_APPROVAL,
  });

  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const handleRowClick = (event: IAdditionalMailing) => {
    const { id } = event;
    navigate(`/additional-mailings/${id}/details`);
  };

  const handleSecondaryTabChange = (tab: string) => {
    const newParams = new URLSearchParams({
      view: tab,
    });
    setSearchParams(newParams);
  };

  const columns = useMemo(() => getColumns(view), [view]);
  const columnData = useMemo(() => {
    return getColumnData(view, {
      getSort,
      onOrderingChange,
    });
  }, [view, getSort, onOrderingChange]);
  const rowData = getRowData(view, data?.results || [], handleRowClick);

  return (
    <PageContainer>
      <Text variant="h4" sx={{ pb: 4, pt: 10, px: 10 }}>
        Additional Mailings
      </Text>
      <ScrollContainer sx={{ pt: 4, pb: 10, pl: 10, pr: 6.25 }}>
        <Stack>
          <SecondaryTabs
            sx={{ mb: 8, mr: "auto" }}
            tabs={[
              {
                label: "Pending Approval",
                value: ADDITIONAL_MAILINGS_VIEW.PENDING_APPROVAL,
                icon:
                  view === ADDITIONAL_MAILINGS_VIEW.PENDING_APPROVAL ? (
                    <HourglassEmpty style={{ color: "#1968FF" }} />
                  ) : (
                    <HourglassEmptyOutlined />
                  ),
              },
              {
                label: "Recently Approved",
                value: ADDITIONAL_MAILINGS_VIEW.RECENTLY_APPROVED,
                icon:
                  view === ADDITIONAL_MAILINGS_VIEW.RECENTLY_APPROVED ? (
                    <Verified style={{ color: "#1968FF" }} />
                  ) : (
                    <VerifiedOutlined />
                  ),
              },
              {
                label: "Historical",
                value: ADDITIONAL_MAILINGS_VIEW.HISTORICAL,
                icon:
                  view === ADDITIONAL_MAILINGS_VIEW.HISTORICAL ? (
                    <History style={{ color: "#1968FF" }} />
                  ) : (
                    <HistoryOutlined />
                  ),
              },
            ]}
            activeTab={view}
            clickHandler={handleSecondaryTabChange}
          />
        </Stack>
        <Table
          count={data?.count || 0}
          columnData={columnData}
          columns={columns}
          isFetching={isFetching}
          isLoading={isLoading}
          noResultsData={{
            title: "No Additional Mailings found",
            subtitle:
              "Sorry, there are no Additional Mailings that match your applied filters.",
          }}
          paginateProps={paginateProps}
          rows={rowData}
          tableMinWidth={1100}
          title={
            view === ADDITIONAL_MAILINGS_VIEW.PENDING_APPROVAL
              ? "Pending Approval Additional Mailings"
              : view === ADDITIONAL_MAILINGS_VIEW.RECENTLY_APPROVED
              ? "Recently Approved Additional Mailings"
              : "Historical Additional Mailings"
          }
          titleVariant="subtitle2"
        />
      </ScrollContainer>
    </PageContainer>
  );
}
